import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import SubItemDetailPane from '@atom/components/common/subItemTree/SubItemDetailPane';
import SubItemsContext from '@atom/components/common/subItemTree/SubItemsContext';
import SubItemTree from '@atom/components/common/subItemTree/SubItemTree';
import { SubItemTreeViewState } from '@atom/components/common/subItemTree/subItemTreeReducer';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Progress } from '@atom/mui';
import { WorkOrderDetailType } from '@atom/types/work';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import useWorkOrderAssetTree from './useWorkOrderAssetTree';
import WorkOrderAssetHeader from './WorkOrderAssetHeader';

import classes from './workOrderAssetView.module.css';

type WorkOrderAssetViewProps = {
  workOrderDetail: WorkOrderDetailType;
  rootAssetId: string;
  setActiveWorkOrderAsset: (id: string) => void;
  refetch: () => void;
  previewView?: boolean;
  taskId?: string;
  viewState?: SubItemTreeViewState;
  onViewStateChange?: (
    rootAssetId: string,
    viewState: SubItemTreeViewState,
  ) => void;
};

const WorkOrderAssetView = ({
  workOrderDetail,
  rootAssetId,
  setActiveWorkOrderAsset,
  refetch,
  previewView = false,
  taskId,
  viewState,
  onViewStateChange,
}: WorkOrderAssetViewProps) => {
  const { schemas = [] } = useSchemas();

  const [selectedId, setSelectedId] = useState<string>(
    viewState?.selectedId ?? rootAssetId,
  );
  const [hasUnsavedChanges, setUnsavedChanges] = useState<boolean>(false);

  const { value, root, loading, saving, approveAll } = useWorkOrderAssetTree({
    workOrderDetail,
    rootAssetId,
    selectedId,
    setSelectedId,
    refetch,
    previewView,
    taskId,
    setUnsavedChanges,
    initialExpanded: viewState?.expanded,
  });

  const { expanded } = value.tree;

  useEffect(() => {
    onViewStateChange?.(rootAssetId, { expanded, selectedId });
  }, [onViewStateChange, rootAssetId, expanded, selectedId]);

  const isWorkOrderAsset = workOrderDetail.inventoryAssetId === rootAssetId;
  const rootSchema = schemas.find(schema => schema.id === root?.schemaId);

  const rootNode = useMemo(
    () =>
      root && {
        node: root,
        icon: isWorkOrderAsset
          ? schemaUtilities.getSchemaIconFromSchemaOrAsset(rootSchema, {
              imgOffset: '0',
              iconSize: '34px',
              marginTop: '0',
            })
          : undefined,
      },
    [root, isWorkOrderAsset, rootSchema],
  );

  return (
    <SubItemsContext.Provider value={{ ...value, rootNode }}>
      <div className={classes.workOrderAssetContainer}>
        <WorkOrderAssetHeader
          root={root}
          saving={saving}
          hasUnsavedChanges={hasUnsavedChanges}
          previewView={previewView}
          onBack={() => setActiveWorkOrderAsset('')}
          onApproveAll={approveAll}
        />
        <div className={classes.workOrderContent}>
          {loading ? (
            <div className={classes.spinner}>
              <Progress />
            </div>
          ) : (
            <>
              <div
                className={clsx(classes.pane, classes.left)}
                data-cy="workOrderAssetTreePane"
              >
                <SubItemTree />
              </div>
              <div className={classes.pane} data-cy="workOrderAssetDetailPane">
                <SubItemDetailPane key={selectedId} />
              </div>
            </>
          )}
        </div>
      </div>
    </SubItemsContext.Provider>
  );
};

export default WorkOrderAssetView;
